import { HttpBackend, provideHttpClient, withInterceptors } from '@angular/common/http';
import { Injectable, importProvidersFrom, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import {
  BrowserModule,
  HAMMER_GESTURE_CONFIG,
  HammerGestureConfig,
  HammerModule,
  bootstrapApplication,
} from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { Geolocation } from '@capacitor/geolocation';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage-angular';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule, routerReducer } from '@ngrx/router-store';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { AVAILABILITY_DATA_SERVICE, SharedUtilAvailabilityModule } from '@vi-succ/availability';
import { DswToastModule } from '@vi-succ/design-system-web';
import {
  PICKUP_WAREHOUSE_MODULE_CONFIG,
  PickupWarehouseDetailModule,
  LocalizedDatePipe as PickupWarehouseLocalizedDatePipe,
} from '@vi-succ/pickup-warehouse-detail';
import { PickupWarehouseDomainModule } from '@vi-succ/pickup-warehouse-domain';
import { LocalizedDatePipe, PRODUCT_DETAIL_MODULE_CONFIG, ProductsDetailModule } from '@vi-succ/products-detail';
import { ProductsDomainModule, activeCatalogSelector } from '@vi-succ/products-domain';
import {
  CART_DETAIL_MODULE_CONFIG,
  LocalizedDatePipe as CartLocalizedDatePipe,
  ShoppingcartCartDetailModule,
} from '@vi-succ/shoppingcart';
import { ShoppingcartDomainModule } from '@vi-succ/shoppingcart-domain';
import { SparepartDomainModule } from '@vi-succ/sparepart-domain';
import { PRICE_DATA_SERVICE, SharedUtilPricesModule, priceModuleConfig } from '@vi-succ/util-prices';
import { ViHeaderDomainModule } from '@vi-succ/vi-header-domain';
import { SafeArea } from 'capacitor-plugin-safe-area';
import hammer from 'hammerjs';
import { filter, first, forkJoin, map, shareReplay, startWith, withLatestFrom } from 'rxjs';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { AppConfigService } from './app/core/config/services/app-config.service';
import { MatDayjsDateModule } from './app/core/dayjs-adapter/dayjs-date.module';
import { LocaleModule } from './app/core/locale/locale.module';
import { DEFAULT_LOCALE } from './app/core/locale/services/locale.service';
import { MultiTranslateHttpFallbackLoader } from './app/core/locale/services/multi-translate-http-fallback-loader';
import { ProtectedTranslateService } from './app/core/locale/services/protected-translate.service';
import { LoginModule } from './app/core/login/login.module';
import { AuthService } from './app/core/login/services/auth.service';
import { selectUserContextInfo } from './app/core/login/state';
import { TrackingModule } from './app/core/tracking/tracking.module';
import { environment } from './app/environments/environment';
import { authorizationInterceptor } from './app/interceptors/authorization.interceptor';
import { errorLoggingInterceptor } from './app/interceptors/error-logging.interceptor';
import { maintenanceInterceptor } from './app/interceptors/maintenance.interceptor';
import { BrowserService } from './app/services/browser.service';
import { DeviceService } from './app/services/device.service';
import { EtAvailabilityDataService } from './app/services/et-availability-data.service';
import { EtCameraDeviceDataService } from './app/services/et-camera-device-data.service';
import { EtPickupLocationDataService } from './app/services/et-pickup-location-data.service';
import { EtProductBomDataService } from './app/services/et-product-bom-data.service';
import { EtProductCompatibilityDataService } from './app/services/et-product-compatibility-data.service';
import { EtProductDetailDataService } from './app/services/et-product-detail.data.service';
import { EtProductErrorCodeDataService } from './app/services/et-product-error-code-data.service';
import { EtProductPriceDataServiceService } from './app/services/et-product-price-data-service.service';
import { EtProductUsageDataService } from './app/services/et-product-usage-data.service';
import { EtProductWearpartDataService } from './app/services/et-product-wearpart-data.service';
import { EtShoppingCartDataService } from './app/services/et-shopping-cart-data.service';
import { EtSparepartDataService } from './app/services/et-sparepart-data.service';
import { NavigationService } from './app/services/navigation.service';
import { LinkFactory } from './app/util';

export function createTranslateLoader(http: HttpBackend) {
  return new MultiTranslateHttpFallbackLoader(http, [
    // your app i18n assets
    {
      prefix: './assets/i18n/viparts/',
      suffix: '.json',
    },
    // @vi-succ/design-system-web assets
    {
      prefix: './assets/i18n/dsw/',
      suffix: '.json',
    },
  ]);
}

@Injectable({
  providedIn: 'root',
})
export class MyHammerConfig extends HammerGestureConfig {
  override overrides = {
    swipe: { direction: hammer.DIRECTION_VERTICAL },
    pinch: { enable: false },
    rotate: { enable: false },
  };
}

SafeArea.getSafeAreaInsets().then((data) => {
  const { insets } = data;
  document.body.style.setProperty('--ion-safe-area-top', `${insets.top}px`);
  document.body.style.setProperty('--ion-safe-area-right', `${insets.right}px`);
  document.body.style.setProperty('--ion-safe-area-bottom', `${insets.bottom}px`);
  document.body.style.setProperty('--ion-safe-area-left', `${insets.left}px`);
});

const domainModules = [
  ShoppingcartDomainModule.forRoot({
    deviceService: DeviceService,
    authService: AuthService,
    shoppingCartDataService: EtShoppingCartDataService,
    pickupLocationDataService: EtPickupLocationDataService,
    productAvailabilityDataService: EtAvailabilityDataService,
    navigationService: NavigationService,
    navigationUrl: '/cart',
  }),
  SparepartDomainModule.forRoot({
    deviceService: DeviceService,
    cameraDeviceDataService: EtCameraDeviceDataService,
    sparepartDataService: EtSparepartDataService,
    navigationUrl: '/detail/:id',
    overviewUrl: '/overview',
  }),
  ProductsDomainModule.forRoot({
    deviceService: DeviceService,
    cartDataService: EtShoppingCartDataService,
    detailDataService: EtProductDetailDataService,
    wearpartDataService: EtProductWearpartDataService,
    errorCodeDataService: EtProductErrorCodeDataService,
    usageDataService: EtProductUsageDataService,
    bomDataService: EtProductBomDataService,
    compatibilityDataService: EtProductCompatibilityDataService,
    baseRoute: '/detail/:id',
    authService: AuthService,
  }),
  ShoppingcartCartDetailModule.forRoot([CartLocalizedDatePipe]),
  ProductsDetailModule.forRoot([LocalizedDatePipe]),
  PickupWarehouseDetailModule.forRoot([
    {
      provide: PICKUP_WAREHOUSE_MODULE_CONFIG,
      useFactory: (authService: AuthService) => {
        return {
          showAvailability$: authService.authenticated$.pipe(shareReplay(1)),
          showStock$: authService.isEmployee$.pipe(shareReplay(1)),
          getCurrentPosition: () =>
            new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject);
            }),
        };
      },
      deps: [AuthService],
    },
    PickupWarehouseLocalizedDatePipe,
  ]),
  PickupWarehouseDomainModule.forRoot({
    pickupWarehouseDataService: EtPickupLocationDataService,
    productAvailabilityDataService: EtAvailabilityDataService,
  }),
  ViHeaderDomainModule.forRoot({
    baseUrl: environment.iam.auth.baseUrl,
    appId: environment.iam.auth.appId,
    accountAppUrl: environment.backend.accountUrl,
    partnerPortalBaseUrl: environment.backend.partnerPortalUrl,
  }),
];
const ngrxModules = [
  StoreModule.forRoot({
    router: routerReducer,
  }),
  !environment.production ? StoreDevtoolsModule.instrument({ connectInZone: true }) : [],
  EffectsModule.forRoot(),
  StoreRouterConnectingModule.forRoot(),
];
const sharedModules = [SharedUtilPricesModule, DswToastModule, SharedUtilAvailabilityModule];
const uiModules = [DswToastModule];
const coreModules = [
  LoginModule.forRoot(),
  TrackingModule.forRoot(),
  LocaleModule,
  TranslateModule.forRoot({
    defaultLanguage: DEFAULT_LOCALE,
    loader: {
      provide: TranslateLoader,
      useFactory: createTranslateLoader,
      deps: [HttpBackend],
    },
  }),
];
const materialModules = [
  MatDialogModule,
  // SelectModule is used in the catalog switcher dialog and apparently needs to be imported here, but no idea why.
  // Throws an error otherwise.
  MatSelectModule,
  MatSnackBarModule,
  MatDayjsDateModule,
];

const showAvailabilityFactory = (authService: AuthService, store: Store) => {
  return authService.authenticated$.pipe(
    withLatestFrom(
      store
        .select(selectUserContextInfo)
        .pipe(map((contextInfo) => contextInfo?.roles.includes('liteaccount') ?? false)),
    ),
    map(([authenticated, isLiteAccount]) => authenticated && !isLiteAccount),
    shareReplay(1),
  );
};

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      IonicModule.forRoot(),
      IonicStorageModule.forRoot(),
      AppRoutingModule,
      HammerModule,
      ServiceWorkerModule.register('./sw.js', {
        enabled: !isDevMode(),
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000',
      }),
      ...ngrxModules,
      ...sharedModules,
      ...coreModules,
      ...domainModules,
      ...materialModules,
      ...uiModules,
    ),
    AppConfigService,
    provideAppInitializer(() => {
      const appConfigService = inject(AppConfigService);
      return appConfigService.loadConfig();
    }),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideHttpClient(withInterceptors([authorizationInterceptor, errorLoggingInterceptor, maintenanceInterceptor])),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      useClass: EtProductPriceDataServiceService,
      provide: PRICE_DATA_SERVICE,
    },
    {
      provide: priceModuleConfig,
      useFactory: (authService: AuthService, translate: TranslateService) => {
        return {
          locale$: translate.onLangChange.pipe(
            map((event) => {
              return event.lang;
            }),
            startWith(translate.currentLang),
          ),
          showPrice$: authService.authenticated$,
        };
      },
      deps: [AuthService, TranslateService],
    },
    {
      provide: PRODUCT_DETAIL_MODULE_CONFIG,
      useFactory: (authService: AuthService, browserService: BrowserService, store: Store) => {
        return {
          canOrder$: authService.canOrder$.pipe(shareReplay(1)),
          showAvailability$: showAvailabilityFactory(authService, store),
          viBooksCallback: (materialNumber: string) => {
            forkJoin([
              authService.authenticated$.pipe(
                map((authenticated) => {
                  return authenticated ? authService.getCsrfToken() : null;
                }),
                first(),
              ),
              store.select(activeCatalogSelector).pipe(
                filter((catalog) => !!catalog),
                first(),
              ),
            ]).subscribe(([token, catalog]) => {
              if (!catalog) {
                return;
              }
              let url = LinkFactory.vibooksLink(catalog, materialNumber);
              if (token) {
                url += `&sessionid=1#token=${token}`;
              }
              browserService.open(url, '_blank', 'Viessmann ViBooks');
            });
          },
        };
      },
      deps: [AuthService, BrowserService, Store],
    },
    {
      provide: CART_DETAIL_MODULE_CONFIG,
      useFactory: (authService: AuthService, store: Store) => {
        return {
          getCurrentPosition: async () => await Geolocation.getCurrentPosition(),
          showStock$: authService.isEmployee$,
          showAvailability$: showAvailabilityFactory(authService, store),
        };
      },
      deps: [AuthService, Store],
    },
    {
      provide: AVAILABILITY_DATA_SERVICE,
      useClass: EtAvailabilityDataService,
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: MyHammerConfig,
    },
    provideAnimations(),
    { provide: TranslateService, useClass: ProtectedTranslateService },
    { provide: ProtectedTranslateService, useExisting: TranslateService },
  ],
}).catch((err) => console.error(err));
